import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiFolder, FiCheck, FiX } from 'react-icons/fi';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './categoriesList.css';

const ViewCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [addError, setAddError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editError, setEditError] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryNameToDelete, setCategoryNameToDelete] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/category');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, refreshTrigger]);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  //Add category
  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) {
      setAddError('Category name is required');
      return;
    }
    setAddError('');
    setIsSubmitting(true);
    try {
      await axios.post('/category', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim()
      });
      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowNewCategoryForm(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        'Failed to create category';
      setAddError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  //Edit 
  const handleStartEdit = (cat) => {
    setEditingId(cat.category_id);
    setEditName(cat.name);
    setEditDescription(cat.description || '');
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditError('');
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim()) {
      setEditError('Category name is required');
      return;
    }
    try {
      await axios.put(`/category/${id}`, {
        name: editName.trim(),
        description: editDescription.trim()
      });
      setEditingId(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        'Failed to update category';
      setEditError(message);
    }
  };

  // ----- Delete category -----
  const handleDeleteClick = (id, name) => {
    setCategoryToDelete(id);
    setCategoryNameToDelete(name);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/category/${categoryToDelete}`);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error('Error deleting category:', err);
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1>Categories</h1>
        <div className="inventory-controls">
          <div className="search-input-container">
            <FiSearch />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            className="add-product-btn"
            onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
          >
            <FiPlus /> Add Category
          </button>
        </div>
      </div>

      {showNewCategoryForm && (
        <div className="new-category-form">
          {addError && <div className="inline-form-error">{addError}</div>}
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name"
            className="category-input"
            disabled={isSubmitting}
          />
          <input
            type="text"
            value={newCategoryDescription}
            onChange={(e) => setNewCategoryDescription(e.target.value)}
            placeholder="Description (optional)"
            className="category-input"
            disabled={isSubmitting}
          />
          <button
            type="button"
            className="submit-category-btn"
            onClick={handleAddNewCategory}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Category'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading categories...</div>
      ) : filteredCategories.length === 0 ? (
        <div className="empty-state">
          <FiFolder className="empty-state-icon" />
          <p>No categories found.</p>
        </div>
      ) : (
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((cat) => {
              const isEditing = editingId === cat.category_id;
              return (
                <tr key={cat.category_id}>
                  {isEditing ? (
                    <>
                      <td colSpan={2}>
                        {editError && <div className="inline-form-error">{editError}</div>}
                        <div className="edit-row-form">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="category-input"
                            placeholder="Category name"
                          />
                          <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="category-input"
                            placeholder="Description (optional)"
                          />
                        </div>
                      </td>
                      <td>
                        {cat.created_at
                          ? new Date(cat.created_at).toLocaleDateString('en-IN')
                          : 'N/A'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => handleSaveEdit(cat.category_id)}
                            title="Save"
                          >
                            <FiCheck />
                          </button>
                          <button
                            className="delete-btn"
                            onClick={handleCancelEdit}
                            title="Cancel"
                          >
                            <FiX />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        <div className="product-name-container">
                          <span className="product-name-text">{cat.name}</span>
                        </div>
                      </td>

                      <td className="description-cell">
                        {cat.description || <span className="no-description">No description</span>}
                      </td>

                      <td>
                        {cat.created_at
                          ? new Date(cat.created_at).toLocaleDateString('en-IN')
                          : 'N/A'}
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => handleStartEdit(cat)}
                            title="Edit category"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteClick(cat.category_id, cat.name)}
                            title="Delete category"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryNameToDelete}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default ViewCategories;